import { create } from 'zustand';
import { OpponentTradeData, TradeOperationHistory } from '../types/interfaces';
import { signalRService } from '../utils/signalr/signalRService';
import {
  normalizeSendTradePayload,
  parseHubGameRoomId,
} from '../utils/signalr/hubGameRoomId';

const defaultOpponentTradeData: OpponentTradeData = {
  TradeCount: {},
  ProfitLoss: {},
  Funds: 1_000_000,
  TotalBalance: 1_000_000,
  SymbolTradeState: {},
  CurrentIndex: 0,
};

interface ChartSyncState {
  opponentReadyTick: number;
  syncGameStartAt: number | null;
}

const defaultChartSync: ChartSyncState = {
  opponentReadyTick: 0,
  syncGameStartAt: null,
};

interface GameSessionState {
  opponentTradeData: OpponentTradeData;
  opponentTradeOperationHistory: TradeOperationHistory[];
  opponentLeftGame: boolean;
  chartSync: ChartSyncState;

  setOpponentTradeData: (data: OpponentTradeData) => void;
  appendTradeHistory: (history: TradeOperationHistory[]) => void;
  setOpponentLeftGame: (left: boolean) => void;
  notifyOpponentSceneReady: () => void;
  onSyncGameStart: (startTimestamp: number) => void;
  playerSceneReady: (roomId: string) => Promise<void>;
  enterBattle: () => Promise<void>;
  leaveBattle: (roomId: string) => Promise<void>;
  sendTrade: (payload: unknown) => void;
  reset: () => void;
}

export const useGameSessionStore = create<GameSessionState>((set) => ({
  opponentTradeData: defaultOpponentTradeData,
  opponentTradeOperationHistory: [],
  opponentLeftGame: false,
  chartSync: defaultChartSync,

  setOpponentTradeData: (data) => set({ opponentTradeData: data }),

  appendTradeHistory: (history) =>
    set((s) => ({
      opponentTradeOperationHistory: [...s.opponentTradeOperationHistory, ...history],
    })),

  setOpponentLeftGame: (left) => set({ opponentLeftGame: left }),

  notifyOpponentSceneReady: () =>
    set((s) => ({
      chartSync: {
        ...s.chartSync,
        opponentReadyTick: s.chartSync.opponentReadyTick + 1,
      },
    })),

  onSyncGameStart: (startTimestamp) =>
    set((s) => ({
      chartSync: {
        ...s.chartSync,
        syncGameStartAt: startTimestamp,
      },
    })),

  playerSceneReady: async (roomId) => {
    await signalRService.invoke('PlayerSceneReady', parseHubGameRoomId(roomId));
  },

  enterBattle: async () => {
    await signalRService.invoke('UserInBattle');
  },

  leaveBattle: async (roomId) => {
    await signalRService.invoke('LeaveGame', parseHubGameRoomId(roomId));
  },

  sendTrade: (payload) => {
    signalRService.invoke('SendTrade', normalizeSendTradePayload(payload));
  },

  reset: () =>
    set({
      opponentTradeData: defaultOpponentTradeData,
      opponentTradeOperationHistory: [],
      opponentLeftGame: false,
      chartSync: defaultChartSync,
    }),
}));
